/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Button from "./ui/Button";
import Icon from "./ui/Icon";
import Text from "./ui/Text";
import Flex from "./ui/Flex";
import { useTranslate } from "../hooks/useTranslate";

const Wrapper = styled(Flex)`
  width: 100%;
`;

const Stepper = styled(Flex)`
  flex: 1;
  background: rgba(${({ theme }) => theme.colors.border.rgb}, 0.5);
  border-radius: 0.5rem;
  padding: 0.125rem;
`;

const QtyText = styled(Text)`
  min-width: 28px;
  text-align: center;
`;

const AddToCartControls = ({
  inCart,
  qty = 0,
  canEditQty = false,
  onAdd,
  onUpdate,
  onRemove,
  min = 1,
  max,
  disabled = false,
  addedLabel = "Added to cart",
}) => {
  const { _t } = useTranslate();

  if (!inCart) {
    return (
      <Button
        icon={<Icon.Plus />}
        title="Add"
        onClick={onAdd}
        variant="outline"
        fullWidth
        disabled={disabled}
      />
    );
  }

  if (!canEditQty) {
    return (
      <Button
        icon={<Icon.Check />}
        title={addedLabel}
        aria-label={_t("Remove from cart")}
        onClick={onRemove}
        variant="secondary"
        fullWidth
        disabled={disabled}
      />
    );
  }

  return (
    <Wrapper $align="center" $gap="0.5rem">
      <Stepper $align="center" $justify="space-between">
        <Button
          icon={<Icon.Minus />}
          aria-label={_t("Decrease quantity")}
          variant="outline"
          size="iconLarge"
          onClick={() => (+qty <= min ? onRemove?.() : onUpdate?.(+qty - 1))}
          disabled={disabled}
        />
        <QtyText color="dark" weight="bold" size="large">{qty}</QtyText>
        <Button
          icon={<Icon.Plus />}
          aria-label={_t("Increase quantity")}
          variant="outline"
          size="iconLarge"
          onClick={() => onUpdate?.(+qty + 1)}
          disabled={disabled || (max != null && +qty >= max)}
        />
      </Stepper>
      <Button
        icon={<Icon.Trash color="iconStroke"/>}
        aria-label={_t("Remove from cart")}
        variant="outline"
        size="iconLarge"
        onClick={onRemove}
        disabled={disabled}
      />
    </Wrapper>
  );
};

export default AddToCartControls;
